import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSmartLinkContext } from '@atlaskit/link-provider/use-smart-link-context';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { collectSmartLinks } from '../utils/collectSmartLinks.ts';
import { getSmartLinkProductGroupLabel } from '../utils/groupSmartLinksByProduct.ts';
import { applySmartLinkRefresh, fetchProductSmartLinkData } from '../utils/smartLinkRefresh.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import { useBylinePageAdf, type BylinePageAdf } from './useBylinePageAdf.ts';
import { useDependencyHealthAnalytics } from './useDependencyHealthAnalytics.ts';

const ADF_WAIT_TIMEOUT_MS = 10000;

export type UseSmartLinksResult = {
	links: EnhancedSmartLink[];
	collectedForContentId: string | undefined;
	loading: boolean;
	refreshing: boolean;
	collectedAdf: BylinePageAdf['adf'];
	documentOutcome?: BylinePageAdf['documentOutcome'];
	error: Error | null;
	refreshLinkUrl: (item: EnhancedSmartLink) => Promise<EnhancedSmartLink | undefined>;
};

const reportSmartLinkError = (err: unknown) => {
	const errorObj = err instanceof Error ? err : new Error(String(err));
	getMonitoringClient().submitError(errorObj, {
		attribution: Attribution.INTEGRATIONS,
	});
	return errorObj;
};

export function useSmartLinks(contentId?: string): UseSmartLinksResult {
	const reportHealth = useDependencyHealthAnalytics();
	const pageDocument = useBylinePageAdf(contentId);
	const adf = pageDocument.contentId === contentId ? pageDocument.adf : null;
	const { connections, store } = useSmartLinkContext();
	const [result, setResult] = useState<{
		contentId: string | undefined;
		links: EnhancedSmartLink[];
		adf: BylinePageAdf['adf'];
		loading: boolean;
		refreshing: boolean;
		hasResolved: boolean;
		error: Error | null;
	}>({
		contentId,
		links: [],
		adf: null,
		loading: true,
		refreshing: false,
		hasResolved: false,
		error: null,
	});
	const linksRef = useRef<EnhancedSmartLink[]>([]);
	const currentContentId = useRef(contentId);
	currentContentId.current = contentId;
	const storeRef = useRef(store);
	useEffect(() => {
		storeRef.current = store;
	}, [store]);

	useEffect(() => {
		linksRef.current = [];
	}, [contentId]);

	useEffect(() => {
		let cancelled = false;
		setResult((previous) => {
			const samePage = previous.contentId === contentId;
			return {
				contentId,
				links: samePage ? previous.links : [],
				adf: samePage ? previous.adf : null,
				hasResolved: samePage && previous.hasResolved,
				loading: !samePage || !previous.hasResolved,
				refreshing: true,
				error: null,
			};
		});
		if (!adf) {
			const timeout = setTimeout(() => {
				setResult((previous) => ({ ...previous, loading: false, refreshing: false }));
			}, ADF_WAIT_TIMEOUT_MS);
			return () => clearTimeout(timeout);
		}

		collectSmartLinks(adf, storeRef.current, connections.client, reportHealth)
			.then((links) => {
				if (cancelled) {
					return;
				}
				linksRef.current = links;
				setResult({
					contentId,
					links,
					adf,
					loading: false,
					refreshing: false,
					hasResolved: true,
					error: null,
				});
			})
			.catch((err: unknown) => {
				if (cancelled) {
					return;
				}
				const error = reportSmartLinkError(err);
				setResult((previous) => ({ ...previous, loading: false, refreshing: false, error }));
			});
		return () => {
			cancelled = true;
		};
	}, [adf, contentId, connections.client, reportHealth]);

	const refreshLinkUrl = useCallback(
		async (item: EnhancedSmartLink): Promise<EnhancedSmartLink | undefined> => {
			const productLabel = getSmartLinkProductGroupLabel(item);
			const refreshedDataByUrl = await fetchProductSmartLinkData(
				linksRef.current,
				productLabel,
				connections.client,
				reportSmartLinkError,
				reportHealth,
			);

			if (currentContentId.current !== contentId || refreshedDataByUrl.size === 0) {
				return undefined;
			}

			// Collection can finish during refresh; merge into its latest links, not the request snapshot.
			const { links: refreshedLinks, matchingItem } = applySmartLinkRefresh(
				linksRef.current,
				item,
				productLabel,
				refreshedDataByUrl,
			);

			linksRef.current = refreshedLinks;
			setResult((previous) => ({ ...previous, links: refreshedLinks }));
			return matchingItem;
		},
		[connections.client, contentId, reportHealth],
	);

	const links = useMemo(
		() => (result.contentId === contentId ? result.links : []),
		[result.contentId, result.links, contentId],
	);
	const samePage = result.contentId === contentId;
	return {
		links,
		collectedForContentId: samePage && result.hasResolved ? contentId : undefined,
		collectedAdf: samePage ? result.adf : null,
		documentOutcome: pageDocument.documentOutcome ?? (adf ? 'available' : 'unknown'),
		loading: !samePage || result.loading,
		refreshing: !samePage || result.refreshing,
		error: samePage ? result.error : null,
		refreshLinkUrl,
	};
}
